import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { primaryColor, secondaryColorThree, secondaryColorTwo, white } from "../../../config";
import VideoPlayButton from "./play";
import nadaYogaImg from '../../../resources/images/sanskrit_mantra.jpeg'
import IntroductionWithImage from "../../../components/IntroductionWithImage";
import { nadaYogaPageIntroduction } from "../../../data/data";
import classicalMusic from '../../../resources/images/veena_classical_music.jpeg'
import bajan from '../../../resources/images/bajan.png'
import Eyebrow from "../../../components/Eyebrow";
import NadaBenefits from "./NadaBenefits";



const colors = {
  bg: "#f6f0e7",
  paper: primaryColor,
  muted: secondaryColorThree,
  gold: "#c9a66b",
  line: secondaryColorThree,
  card: primaryColor,
};

const offerings = [
  {
    number: "01",
    title: "Chant",
    text: "Explore Sanskrit chanting through pronunciation, breath, pitch, rhythm, resonance and attentive repetition.",
    img: nadaYogaImg
  },
  {
    number: "02",
    title: "Bhajan",
    text: "Experience collective singing where individual voices meet rhythm, listening and participation.",
    img: bajan
  },
  {
    number: "03",
    title: "Classical Music",
    text: "Train the ear and voice through svara, tāla, rāga, listening and disciplined musical practice.",
    img: classicalMusic
  },
];

const pillars = [
  {
    title: "Sound",
    text: "Hear distinctions. Refine pronunciation. Respect the structure of the language.",
  },
  {
    title: "Breath",
    text: "Notice how inhalation, exhalation and sustained voice shape the experience.",
  },
  {
    title: "Attention",
    text: "Use listening and repetition as a practical training in perception.",
  },
  {
    title: "Experience",
    text: "Observe directly, without requiring belief or dismissing what you have not yet explored.",
  },
];

interface ActionButtonProps {
  children: React.ReactNode;
  primary?: boolean;
  href?: string;
}

function ActionButton({ children, primary = false, href = "#" }: ActionButtonProps) {
  return (
    <Button
      as="a"
      href={href}
      variant="unstyled"
      h="auto"
      px={5}
      py={3}
      border="1px solid"
      borderColor={primary ? colors.paper : colors.line}
      borderRadius="999px"
      bg={primary ? colors.paper : "rgba(255,255,255,.03)"}
      color={primary ? "#111" : colors.paper}
      fontFamily="Arial, sans-serif"
      fontSize="12px"
      fontWeight="600"
      letterSpacing="0.14em"
      textTransform="uppercase"
      transition="all .25s ease"
      _hover={{
        bg: primary ? secondaryColorTwo : "rgba(255,255,255,.08)",
        borderColor: secondaryColorTwo,
        transform: "translateY(-2px)",
      }}
    >
      {children}
    </Button>
  );
}

function Hero() {
  return (
    <Box
      className="testsrgsrgsgergseg"
      minH="100vh"
      position="relative"
      display="grid"
      placeItems="center"
      bg={colors.bg}
      _before={{
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { base: "100px", sm: "35vmin", md: "58vmin" },
        height: { base: "100px", sm: "35vmin", md: "58vmin" },
        border: { base: `1px solid ${primaryColor}`, md: `1px solid ${primaryColor}` },
        borderRadius: "50%",
        opacity: { base: 0.5, md: 0.3 },
        animation: { base: "mobilePulse 4s ease-in-out infinite", md: "pulse 5s ease-in-out infinite" },
      }}
      _after={{
        content: '""',
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { base: "50px", sm: "16vmin", md: "25vmin" },
        height: { base: "50px", sm: "16vmin", md: "25vmin" },
        border: { base: `2px solid ${secondaryColorThree}`, sm: `6px solid ${secondaryColorThree}`, md: `10px solid ${secondaryColorThree}` },
        borderRadius: "50%",
        opacity: { base: 0.4, md: 0.3 },
        animation: { base: "mobilePulse 4s ease-in-out infinite reverse", md: "pulse 5s ease-in-out infinite reverse" },
      }}
    >
      {/* Radial glow */}
      <Box
        position="absolute"
        inset="0"
        bg={{
          base: `
            radial-gradient(
              circle at 50% 48%,
              rgba(201,166,107,.14),
              transparent 25%
            ),
            radial-gradient(
              circle at 20% 20%,
              rgba(255,255,255,.04),
              transparent 22%
            )
          `,
        }}
      />

      <VStack
        textAlign="center"
        maxW="900px"
        px={6}
        spacing={0}
      >
        <Eyebrow>Chant · Bhajan · Indian Classical Music</Eyebrow>

        <Heading
          as="h1"
          color={colors.paper}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "56px", sm: "72px", md: "96px", lg: "110px" }}
          fontWeight="400"
          lineHeight=".95"
          letterSpacing="-0.035em"
          mb={6}
        >
          Sound Is
          <br />
          an Experience.
        </Heading>
        <VideoPlayButton />
        <Text
          color={secondaryColorThree}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "18px", md: "28px" }}
          lineHeight="1.45"
          maxW="680px"
          mt={{ base: 8, md: 6 }}
        >
          A space to listen closely, learn precisely, chant together and
          explore what sound becomes when it is experienced, not merely heard.
        </Text>

        <Flex
          gap={3}
          mt={10}
          wrap="wrap"
          justify="center"
        >
          <ActionButton primary href="#explore">
            Explore
          </ActionButton>

          <ActionButton href="#offerings">
            The Sessions
          </ActionButton>
        </Flex>
      </VStack>
    </Box>
  );
}

function IntroSection() {
  return (
    <>
      <Container
        as="section"
        id="explore"
        maxW="780px"
        py={{ base: 20, md: 24 }}
        textAlign="center"
      >
        <Heading
          color={colors.paper}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "38px", md: "56px", lg: "64px" }}
          fontWeight="400"
          lineHeight="1.02"
          mb={5}
        >
          We hear sound.
          <br />
          <Box as="em" color={secondaryColorThree} fontStyle="normal">
            But can we experience it?
          </Box>
        </Heading>

        <Text
          color={colors.muted}
          fontSize="20px"
          lineHeight="1.55"
        >
          Sound is physical vibration. Human beings produce it through breath,
          voice and articulation. Indian traditions have spent centuries
          refining the practice of listening, chanting, singing and musical
          attention.
        </Text>
      </Container>
      <Box  >
        <IntroductionWithImage
          img={nadaYogaImg}>
          {nadaYogaPageIntroduction}
          <br />
        </IntroductionWithImage>
      </Box>
    </>

  );
}

interface OfferingCardProps {
  number: string;
  title: string;
  text: string;
  img: any;
}

function OfferingCard({ number, title, text, img }: OfferingCardProps) {
  return (
    <Box
      border="1px solid"
      borderColor={colors.card}
      p={7}
      borderRadius="20px"
      minH="210px"
      transition="all .3s ease"
      _hover={{
        borderColor: secondaryColorThree,
        transform: "translateY(-4px)",
        bg: white,
      }}
    >
      <Text
        color={secondaryColorThree}
        fontFamily="Arial, sans-serif"
        fontSize="11px"
        fontWeight="700"
        letterSpacing=".2em"
      >
        {number}
      </Text>

      <Heading
        color={colors.paper}
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="27px"
        fontWeight="400"
        mt={6}
        mb={2}
      >
        {title}
      </Heading>
      {/* <Image sizes="10px" src={img} alt="Classical Music" /> */}
      <Box display="flex" justifyContent={"center"}>
        <Box
          width={{ base: "100%", lg: "15vw" }}
          height={{ base: "8rem", md: "8rem" }}
          minH={{ base: "auto", lg: "auto" }}
          minW={{ base: "100%", lg: "4rem" }}
          backgroundImage={img}
          backgroundSize="contain"
          bgRepeat={"no-repeat"}
          justifyContent={"center"}
          bgPos={"50%"}
          className="bg-container"
        />
      </Box>
      <br />
      <Text color={secondaryColorThree}>
        {text}
      </Text>
      <br />
      {/* <Button bg={primaryColor} color={"white"}
        className=" text-right-test"
        _hover={{
          backgroundColor: secondaryColorTwo,
        }}
      >Learn</Button> */}
    </Box>
  );
}

function Offerings() {
  return (
    <>
      <Container
        as="section"
        id="offerings"
        maxW="1100px"
        textAlign="center"
        py={{ base: 10, md: 0 }}
      >
        <Heading
          color={colors.paper}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "38px", md: "56px", lg: "64px" }}
          fontWeight="400"
          lineHeight="1.02"
          mb={5}
        >
          Learn the sound.
          <br />
          Then explore it.
        </Heading>

        <Text color={colors.muted} fontSize="20px">
          Sanskrit invites careful attention to articulation, vowel length,
          aspiration, place of pronunciation and rhythm. We begin with the
          sound as it actually is, then observe what the practice does to our
          experience.
        </Text>
        <br />
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          {offerings.map((item) => (
            <OfferingCard key={item.number} {...item} />
          ))}
        </SimpleGrid>
        {/* <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          {offerings1.map(item => item)}
          {offerings1.map(item => item)}
          {offerings1.map(item => item)}
        </SimpleGrid> */}
        <LearningSection />
        <ExplorationSection />
      </Container>
    </>
  );
}

function QuoteSection() {
  return (
    <Container
      as="section"
      maxW="1100px"
      py={{ base: 24, md: 32 }}
      textAlign="center"
    >
      <Text
        color={colors.paper}
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize={{ base: "34px", md: "48px", lg: "54px" }}
        lineHeight="1.12"
      >
        Sound begins with{" "}
        <Box as="em" color={colors.gold} fontStyle="normal">
          precision.
        </Box>
        <br />
        Depth begins with{" "}
        <Box as="em" color={colors.gold} fontStyle="normal">
          listening.
        </Box>
      </Text>
    </Container>
  );
}

interface PillarProps {
  title: string;
  text: string;
}

function Pillar({ title, text }: PillarProps) {
  return (
    <Box
      py={6}
      borderTop="1px solid"
      borderColor={colors.line}
    >
      <Text
        color={colors.paper}
        fontSize="18px"
        fontWeight="600"
        mb={2}
      >
        {title}
      </Text>

      <Text color={colors.muted}>
        {text}
      </Text>
    </Box>
  );
}

function LearningSection() {
  return (
    <Container
      as="section"
      maxW="1100px"
      py={{ base: 20, md: 24 }}
    >
      {/* <Box textAlign="center" maxW="780px" mx="auto">
        <Heading
          color={colors.paper}
          fontFamily="Georgia, 'Times New Roman', serif"
          fontSize={{ base: "38px", md: "56px", lg: "64px" }}
          fontWeight="400"
          lineHeight="1.02"
          mb={5}
        >
          Learn the sound.
          <br />
          Then explore it.
        </Heading>

        <Text color={colors.muted} fontSize="20px">
          Sanskrit invites careful attention to articulation, vowel length,
          aspiration, place of pronunciation and rhythm. We begin with the
          sound as it actually is, then observe what the practice does to our
          experience.
        </Text>
      </Box> */}

      <SimpleGrid
        columns={{ base: 1, md: 2 }}
        gap={4}
        mt={10}
      >
        {pillars.map((pillar) => (
          <Pillar key={pillar.title} {...pillar} />
        ))}
      </SimpleGrid>
    </Container>
  );
}

function ExplorationSection() {
  return (
    <Container
      as="section"
      maxW="780px"

      textAlign="center"
    >
      <Heading
        color={colors.paper}
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize={{ base: "38px", md: "56px" }}
        fontWeight="400"
        lineHeight="1.02"
        mb={5}
      >
        Don't believe.
        <br />
        <Box as="em" color={secondaryColorThree} fontStyle="normal">
          Explore.
        </Box>
      </Heading>

      <Text color={primaryColor} fontSize="20px">
        Come with curiosity
      </Text>
      <Text color={secondaryColorThree} fontSize="20px">
        Listen. Chant. Observe. Explore.
      </Text>
      <Heading
        as="h2"
        m="0"
        fontSize={{
          base: "25px"
        }}
        lineHeight="1.08"
        fontWeight="400"
        letterSpacing="-0.035em"
        color={primaryColor}
      >
        <br />
        The Benefits
      </Heading>
    </Container>
  );
}

function Footer() {
  return (
    <Box
      as="footer"
      textAlign="center"
      py={{ base: 20, md: 28 }}
      px={6}
      borderTop="1px solid"
      borderColor={colors.line}
    >
      <Heading
        color={colors.paper}
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize={{ base: "36px", md: "48px" }}
        fontWeight="400"
        mb={3}
      >
        Sound Is an Experience
      </Heading>

      <Text
        color={colors.muted}
        fontFamily="Arial, sans-serif"
        fontSize="12px"
        letterSpacing=".1em"
        textTransform="uppercase"
      >
        Chant · Bhajan · Indian Classical Music
      </Text>

      <Flex justify="center" mt={8}>
        <ActionButton primary>
          Join the exploration
        </ActionButton>
      </Flex>
    </Box>
  );
}

export default function SoundExperience() {



  return (
    <Box
      bg={colors.bg}
      color={colors.paper}
      minH="100vh"
      fontFamily="Georgia, 'Times New Roman', serif"
      lineHeight="1.55"
      css={{
        "@keyframes pulse": {
          "0%, 100%": {
            transform: "translate(-50%, -50%) scale(.92)",
            opacity: 0.3,
          },
          "50%": {
            transform: "translate(-50%, -50%) scale(1.04)",
            opacity: 1,
          },
        },
        "@keyframes mobilePulse": {
          "0%, 100%": {
            transform: "translate(-50%, -50%) scale(1)",
            opacity: 0.3,
          },
          "50%": {
            transform: "translate(-50%, -50%) scale(1.15)",
            opacity: 0.7,
          },
        },
      }}
    >
      <Hero />
      {/* <MainImageCarousel /> */}

      <main>
        <IntroSection />
        <Offerings />
        <NadaBenefits />
        {/* <QuoteSection /> */}
      </main>

      {/* <Footer /> */}
    </Box>
  );
}
